import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router";

import type { BillingResponse, PlanStatusResponse } from "@pharmacy/contracts";

import { api } from "@/api/client";
import { ApiError } from "@/api/errors";
import { useSession } from "@/auth/session";
import { Button, Dialog } from "@/components/ui";
import { useI18n } from "@/i18n/provider";

/**
 * The pharmacy's plan, for every page (apps/mobile/src/billing/plan.tsx and
 * plan-state.ts): where it stands, the notice to show, and whether anything
 * new may be recorded.
 *
 * Read from GET /billing when the app opens, when the window comes back, and
 * every ten minutes (every thirty seconds once the plan has ended, so a
 * payment shows within a minute). The web is online: it takes the server's
 * own status rather than working it out again.
 *
 * Until plans are charged (`enforced` false): no notices, nothing blocked.
 *
 * View only (the owner's decision, 2026-10-12): the buttons stay where they
 * are. A click on one that would change something says "View only" and does
 * nothing: `stop()` at the start of the click, or the handler wrapped in
 * `guard()`. Whatever slips past is refused by the server with plan_expired,
 * and says the same thing (never a plain error).
 */

export type PlanNotice = "ending" | "grace" | "viewOnly";

interface PlanValue {
  /** The server has answered. */
  known: boolean;
  /** Asking failed (no answer yet): for Billing's error and Try again. */
  error: unknown;
  retry: () => void;
  /** Plans are being charged. */
  enforced: boolean;
  billing: BillingResponse | null;
  status: PlanStatusResponse | null;
  /** Nothing new may be recorded: look, share, and take credit paid back. */
  viewOnly: boolean;
  /** The notice for this person, if any. */
  notice: PlanNotice | null;
  /** At the start of a click that would change something. View only: says so, and returns true. */
  stop: () => boolean;
  /** `fn`, for a button: unless view only, when it says so instead. */
  guard: <A extends unknown[]>(fn: (...args: A) => unknown) => (...args: A) => void;
}

const NONE: PlanValue = {
  known: false,
  error: null,
  retry: () => {},
  enforced: false,
  billing: null,
  status: null,
  viewOnly: false,
  notice: null,
  stop: () => false,
  guard:
    (fn) =>
    (...args) =>
      void fn(...args),
};

const PlanContext = createContext<PlanValue>(NONE);

const EVERY = 10 * 60_000;
const ENDED_EVERY = 30_000;

/**
 *   ending (7 days or fewer left)   owners only
 *   grace (7 days after the end)    owners, and staff ("tell the owner")
 *   view only                       everybody
 */
function noticeFor(billing: BillingResponse | undefined, isOwner: boolean): PlanNotice | null {
  if (!billing?.enforced) return null;
  switch (billing.status.state) {
    case "ending":
      return isOwner ? "ending" : null;
    case "grace":
      return "grace";
    case "readOnly":
      return "viewOnly";
    default:
      return null;
  }
}

export function PlanProvider({ children }: { children: React.ReactNode }) {
  const { state } = useSession();
  const me = state.status === "signedIn" ? state.me : null;
  const isOwner = me?.role === "admin";
  const queryClient = useQueryClient();
  const [stopped, setStopped] = useState(false);

  const query = useQuery({
    queryKey: ["billing", me?.tenantId],
    queryFn: () => api.get<BillingResponse>("/billing"),
    enabled: me !== null,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const s = q.state.data?.status.state;
      return q.state.data?.enforced && (s === "grace" || s === "readOnly") ? ENDED_EVERY : EVERY;
    },
  });
  const billing = query.data ?? null;
  const viewOnly = !!billing?.enforced && billing.status.state === "readOnly";

  // The server refused a change because the plan ended: say so, and ask again (this page is behind).
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type !== "updated" || event.action.type !== "error") return;
        const error = event.action.error;
        if (error instanceof ApiError && error.code === "plan_expired") {
          setStopped(true);
          void queryClient.invalidateQueries({ queryKey: ["billing"] });
        }
      }),
    [queryClient],
  );

  const stop = useCallback(() => {
    if (!viewOnly) return false;
    setStopped(true);
    return true;
  }, [viewOnly]);

  const guard = useCallback<PlanValue["guard"]>(
    (fn) =>
      (...args) => {
        if (!stop()) void fn(...args);
      },
    [stop],
  );

  const value = useMemo<PlanValue>(
    () => ({
      known: billing !== null,
      error: query.error,
      retry: () => void query.refetch(),
      enforced: billing?.enforced ?? false,
      billing,
      status: billing?.status ?? null,
      viewOnly,
      notice: noticeFor(billing ?? undefined, isOwner),
      stop,
      guard,
    }),
    [billing, viewOnly, isOwner, stop, guard, query],
  );

  return (
    <PlanContext.Provider value={value}>
      {children}
      {stopped && <ViewOnlyDialog isOwner={isOwner} onClose={() => setStopped(false)} />}
    </PlanContext.Provider>
  );
}

/** Around every signed-in page. */
export function PlanLayout() {
  return (
    <PlanProvider>
      <Outlet />
    </PlanProvider>
  );
}

/** Outside the signed-in pages (the public terms): nothing charged, nothing blocked. */
export function usePlan(): PlanValue {
  return useContext(PlanContext);
}

/** "View only": what a click on a change does once the plan has ended. Owners can go to Billing. */
function ViewOnlyDialog({ isOwner, onClose }: { isOwner: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <Dialog
      title={t.viewOnly}
      size="confirm"
      onClose={onClose}
      footer={
        <>
          <Button variant={isOwner ? "secondary" : "primary"} label={t.ok} onClick={onClose} />
          {isOwner && (
            <Button
              label={t.seePlans}
              onClick={() => {
                onClose();
                void navigate("/billing");
              }}
            />
          )}
        </>
      }
    >
      <p className="text-base text-body">{isOwner ? t.viewOnlyStopOwner : t.viewOnlyStopStaff}</p>
    </Dialog>
  );
}
